import { formatActivityDate } from "@/utils/constant/dateUtils";
import { PaymentTransaction } from "@/utils/types/PaymentManagement";

interface PaymentManagementLogModalProps {
  paymentTransaction: PaymentTransaction;
}

const PaymentManagementLogModal = ({
  paymentTransaction,
}: PaymentManagementLogModalProps) => {
  if (!paymentTransaction) {
    return null;
  }

  const isMakeContribute =
    paymentTransaction.contribution_type === "Make Contribute";
  const isInitialNPS =
    paymentTransaction.contribution_type === "Initial NPS Contribute";

  const formatCurrency = (value: string | number) => {
    const numValue =
      typeof value === "string" ? parseFloat(value || "0") : value;
    return `₹${numValue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Payment Information Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-800">
          Payment Information
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">Payment ID</span>
            <span className="font-medium text-gray-800">
              {paymentTransaction.ack_id || paymentTransaction.id || "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">User</span>
            <span className="font-medium text-gray-800">
              {paymentTransaction.sub_fst_name ||
                paymentTransaction.user?.email ||
                "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">PRAN</span>
            <span className="font-medium text-gray-800">
              {paymentTransaction.pran_number || "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">Contribution Type</span>
            <span className="font-medium text-gray-800">
              {paymentTransaction.contribution_type || "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">Payment Status</span>
            <span className="font-medium text-gray-800">
              {paymentTransaction.payment_status || "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">Date</span>
            <span className="font-medium text-gray-800">
              {paymentTransaction.created_on
                ? formatActivityDate(paymentTransaction.created_on)
                : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Breakdown Section */}
      <div className="border-t pt-4">
        <h3 className="mb-4 text-lg font-semibold text-gray-800">
          Payment Breakdown
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Contributions Amount
            </span>
            <span className="text-base font-semibold text-gray-800">
              {formatCurrency(
                paymentTransaction.pran_total_contribution || "0"
              )}
            </span>
          </div>

          {/* Charges based on contribution type */}
          {isMakeContribute && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">POP Charges</span>
                <span className="text-sm font-medium text-orange-600">
                  {formatCurrency(paymentTransaction.pop_charges || "0")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">POP Charges GST</span>
                <span className="text-sm font-medium text-orange-600">
                  {formatCurrency(paymentTransaction.gst_on_pop_charges || "0")}
                </span>
              </div>
            </>
          )}

          {isInitialNPS && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Account Opening Charges
                </span>
                <span className="text-sm font-medium text-orange-600">
                  {formatCurrency(
                    paymentTransaction.pran_ac_opening_charges || "0"
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Account Opening Charges GST
                </span>
                <span className="text-sm font-medium text-orange-600">
                  {formatCurrency(
                    paymentTransaction.pran_ac_opening_charges_gst || "0"
                  )}
                </span>
              </div>
            </>
          )}

          {/* Net Contribution */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-gray-800">
                Total Amount
              </span>
              <span className="text-base font-bold text-green-600">
                {formatCurrency(paymentTransaction.total_payable_amount || "0")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentManagementLogModal;
